import { useAtomValue } from 'jotai';
import { holdsIdentityAtom, claimsAtom } from '../../stores/auth/atoms';
import { useCurrentPeriod } from '../../hooks/useBilling';
import type { CurrentPeriodPosition, DailyConsumed } from '@localagent/sdk';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';

export type RunoutState = 'no-usage' | 'on-pace' | 'will-run-out' | 'exhausted';

export interface UsageProjection {
  burnRate: number;
  runoutDate: Date | null;
  state: RunoutState;
}

export function computeProjection(
  dailyConsumed: DailyConsumed[],
  creditsRemaining: number,
  periodEnd: string,
): UsageProjection {
  if (creditsRemaining <= 0) {
    return { burnRate: 0, runoutDate: null, state: 'exhausted' };
  }

  const now = new Date();
  const trailing7Days = dailyConsumed.slice(-7);

  if (trailing7Days.length === 0) {
    return { burnRate: 0, runoutDate: null, state: 'no-usage' };
  }

  const totalCredits = trailing7Days.reduce((sum, d) => sum + d.credits, 0);
  const burnRate = totalCredits / trailing7Days.length;

  if (burnRate === 0) {
    return { burnRate: 0, runoutDate: null, state: 'no-usage' };
  }

  const daysUntilRunout = creditsRemaining / burnRate;
  const runoutDate = new Date(now.getTime() + daysUntilRunout * 24 * 60 * 60 * 1000);
  const periodEndDate = new Date(periodEnd);

  if (runoutDate > periodEndDate) {
    return { burnRate, runoutDate: null, state: 'on-pace' };
  }

  return { burnRate, runoutDate, state: 'will-run-out' };
}

function formatCredits(credits: number): string {
  if (credits >= 1_000_000) {
    return `${(credits / 1_000_000).toFixed(1)}M`;
  }
  if (credits >= 1_000) {
    return `${(credits / 1_000).toFixed(1)}K`;
  }
  return credits.toLocaleString();
}

function formatDate(date: Date): string {
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

export interface DailyChartProps {
  dailyConsumed: DailyConsumed[];
}

export function DailyChart({ dailyConsumed }: DailyChartProps) {
  if (dailyConsumed.length === 0) {
    return (
      <div className="h-24 flex items-center justify-center text-sm text-muted-foreground">
        No usage recorded yet
      </div>
    );
  }

  const maxCredits = Math.max(...dailyConsumed.map((d) => d.credits), 1);

  return (
    <div className="h-24 flex items-end gap-1" data-testid="usage-daily-chart">
      {dailyConsumed.map((day) => {
        const heightPct = (day.credits / maxCredits) * 100;
        const dateLabel = new Date(day.date).toLocaleDateString(undefined, {
          weekday: 'short',
        });
        return (
          <div
            key={day.date}
            className="flex-1 flex flex-col items-center gap-1"
            title={`${dateLabel}: ${formatCredits(day.credits)}`}
          >
            <div
              className="w-full bg-primary/80 rounded-t transition-all"
              style={{ height: `${Math.max(heightPct, 2)}%` }}
            />
            <span className="text-[10px] text-muted-foreground truncate w-full text-center">
              {dateLabel}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export interface UsageProjectionDisplayProps {
  projection: UsageProjection;
  position: CurrentPeriodPosition;
}

export function UsageProjectionDisplay({
  projection,
  position,
}: UsageProjectionDisplayProps) {
  const { burnRate, runoutDate, state } = projection;
  const { overage_amount_cents, credit_price_micro_cents } = position;

  const stateLabels: Record<RunoutState, { text: string; className: string }> = {
    'no-usage': {
      text: 'No usage recorded',
      className: 'text-muted-foreground',
    },
    'on-pace': {
      text: 'On pace to stay within allowance',
      className: 'text-green-700 dark:text-green-400',
    },
    'will-run-out': {
      text: runoutDate
        ? `Projected to exceed allowance by ${formatDate(runoutDate)}`
        : 'Projected to exceed allowance',
      className: 'text-yellow-700 dark:text-yellow-400',
    },
    exhausted: {
      text: `Allowance exhausted · Overage: $${(overage_amount_cents / 100).toFixed(2)}`,
      className: 'text-red-700 dark:text-red-400',
    },
  };

  const { text, className } = stateLabels[state];

  return (
    <div data-testid="usage-runout">
      <div className="flex justify-between items-center">
        <span className="text-sm text-muted-foreground" data-testid="usage-burn-rate">
          {burnRate > 0
            ? `~${formatCredits(Math.round(burnRate))} credits/day`
            : 'No daily average'}
        </span>
        <span className="text-xs text-muted-foreground">
          {credit_price_micro_cents > 0 &&
            `$${((credit_price_micro_cents / 1_000_000) * 1_000_000).toFixed(2)}/M overage`}
        </span>
      </div>
      <p
        className={`text-sm mt-1 ${className}`}
        data-testid="usage-runout-state"
        data-state={state}
      >
        {text}
      </p>
    </div>
  );
}

export function UsageSection() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const claims = useAtomValue(claimsAtom);
  const { data: position, isLoading } = useCurrentPeriod();

  if (!isAuthenticated || !claims?.accountId) {
    return null;
  }

  if (isLoading) {
    return (
      <section className="space-y-4" data-testid="usage-section">
        <div>
          <h2 className="text-lg font-semibold">Usage</h2>
        </div>
        <LoadingState label="Loading usage data..." />
      </section>
    );
  }

  if (!position) {
    return null;
  }

  const projection = computeProjection(
    position.daily_consumed,
    position.credits_remaining,
    position.period_end,
  );

  // Period card + payment action live in BillingSection only — duplicating
  // them here doubles testids (e.g. billing-period-complimentary) on Settings.
  return (
    <section className="space-y-4" data-testid="usage-section">
      <div>
        <h2 className="text-lg font-semibold">Usage</h2>
        <p className="text-sm text-muted-foreground">
          Credit consumption this billing period
        </p>
      </div>

      <div className="rounded-lg bg-card p-4 space-y-3">
        <h3 className="text-sm font-medium text-muted-foreground">
          Daily Consumption
        </h3>
        <DailyChart dailyConsumed={position.daily_consumed} />
        <UsageProjectionDisplay projection={projection} position={position} />
      </div>
    </section>
  );
}
